import { v4 as uuid } from 'uuid';
import {SelectedAssets} from "./itemDnD";

type ColumnedAsset = [string, string, string];
type RowAsset = [string, string];
type TopLevelAsset = [string];

export type Path = ColumnedAsset | RowAsset | TopLevelAsset
export type PathArray = (Path)[]

export enum ItemLevel {
  GROUP = 'group',
  ROW = 'row',
  ASSET = 'asset',
}

export type Asset = {
  created: string;
  data: {[assetDataIndex: string]: any};
  id: string;
  order: number;
  pack_id: string;
  group_id: string;
  row_id: string;
  type: string;
  updated: string;
  level: ItemLevel.ASSET,
  user_id: string;
}

export type Row = {
  id: string;
  level: ItemLevel.ROW,
  children: Asset[]
}

export type Group = {
  id: string;
  level: ItemLevel.GROUP,
  children: Row[] | [],
}

export type Layout = (Group | Asset)[];
export type MutationReturn = [ Layout, Asset[] ];

export function isGroup(item: Asset | Group): item is Group {
  return (item as Group).level === ItemLevel.GROUP;
}

export function isAsset(item: Asset | Group): item is Asset {
  return (item as Asset)?.level === ItemLevel.ASSET;
}

export const orderSelectedAssetsDesc = (paths: PathArray): PathArray => (
  paths.sort((a, b) => {
    if (+a[0] === +b[0]) {
      if (+a[1] === +b[1]) {
        return +b[2] - +a[2];
      }
      return +b[1] - +a[1];
    }
    return +b[0] - +a[0];
  })
);

export const getAssetByPath = (layout: Layout, path: string[]): Asset => {
  if (path.length === 1) {
    return layout[+path[0]] as Asset;
  }

  if (path.length === 3) {
    if ((layout[+path[0]] as Group)?.children) {
      return (layout[+path[0]] as Group)?.children[+path[1]]?.children[+path[2]] as Asset;
    }
  }

  return null;
}

export const getAssetsFromRow = (layout: Layout, path: string[]): Asset[] => {
  if (path.length === 1) {
    return null;
  }

  return (layout[+path[0]] as Group)?.children[+path[1]].children;
}

export const getRowsBeforeDropIndex = (group: Group, dropIndex: number): Row[] => {
  if (dropIndex === 0) {
    return [];
  }

  return group.children.slice(0, dropIndex);
}

export const getAssetsFromGroup = (group: Group): Asset[] => {
  if (!group) {
    return [];
  }

  const assets = [];

  for (const row of group.children) {
    for (const asset of row.children) {
      assets.push(asset);
    }
  }

  return assets;
}

export const removeMultipleAssetsByPathFromLayout = (layout: Layout, paths: PathArray): (Group | Asset)[] => {
  let updatedLayout = [ ...layout ];

  const orderedPaths = orderSelectedAssetsDesc(paths);

  for (const path of orderedPaths) {
    updatedLayout = removeFromLayoutByPath(updatedLayout, path);
  }

  return cleanLayout(updatedLayout);
}

export const cleanRowsInGroup = (group: Group): Group => {
  const rows: Row[] = group.children.filter((row) => row.children.length);

  return {
    ...group,
    children: rows,
  }
} 

// remove any empty groups or rows
export const cleanLayout = (layout: Layout): Layout => {
  const cleanedLayout = [];

  for (const item of layout) {
    if (isGroup(item)) {
      if (item.children.length) {
        const cleanedGroup = cleanRowsInGroup(item)
        cleanedLayout.push(cleanedGroup);
      }
    } else {
      cleanedLayout.push(item);
    }
  }

  return cleanedLayout;
}

export const getDropOrderFromTopLevelIndex = (layout: Layout, index: number): number => {
  if (index === 0) {
    return 0;
  }

  const itemsBeforeIndex: (Group | Asset)[] = layout.slice(0, index);
  const groups: (Group | Asset)[] = itemsBeforeIndex.filter(( item ) => isGroup(item));

  let count = itemsBeforeIndex.length - groups.length;

  for (const group of groups) {
    if (!isGroup(group)) {
      return;
    }

    for (const row of group.children) {
      count += row.children.length;
    }
  }
  
  return count;
}

export const getDropOrderFromSecondLevelIndex = (layout: Layout, dropPath: Path): number => {
  const countAssetsBeforeTopLevelIndex: number = getDropOrderFromTopLevelIndex(layout, +dropPath[0]);
  const targetedGroup: Group | Asset = layout[+dropPath[0]];

  if (!isGroup(targetedGroup)) {
    return countAssetsBeforeTopLevelIndex;
  }
  
  const rows: Row[] = getRowsBeforeDropIndex(targetedGroup, +dropPath[1]);

  if (!rows.length) {
    return countAssetsBeforeTopLevelIndex;
  }

  let count = 0;

  for (const row of rows) {
    count += row.children.length;
  }

  return countAssetsBeforeTopLevelIndex + count;
}

export const getDropOrderFromThirdLevelIndex = (layout: Layout, dropPath: Path): number => {
  const countAssetsBeforeRowIndex = getDropOrderFromSecondLevelIndex(layout, dropPath);
  return countAssetsBeforeRowIndex + +dropPath[2];
}

export const getDropOrderPosition = (layout: Layout, dropPath: Path): number => {
  const clonedLayout = [...layout];
  
  if (dropPath.length === 1) {
    return getDropOrderFromTopLevelIndex(clonedLayout, +dropPath[0])
  }

  if (dropPath.length === 2) {
    return getDropOrderFromSecondLevelIndex(clonedLayout, dropPath);
  }

  if (dropPath.length === 3) {
    return getDropOrderFromThirdLevelIndex(clonedLayout, dropPath);
  }
}

export const removeFromLayoutByPath = (layout: Layout, path: Path): Layout => {
  const clonedLayout = [...layout];

  if (path.length === 1) {
    clonedLayout.splice(+path[0], 1);
  }

  if (path.length === 2) {
    (clonedLayout[+path[0]] as Group).children.splice(+path[1], 1);
  }

  if (path.length === 3) {
    (clonedLayout[+path[0]] as Group).children[+path[1]].children.splice(+path[2], 1);
  }

  return clonedLayout;
}

export const isMovingItemUpwards = (itemPath: Path, dropPath: Path): boolean => {
  if (itemPath.length === 1) {
    return +itemPath[0] > +dropPath[0]
  }
  
  if (itemPath.length === 2) {
    if (+itemPath[0] === +dropPath[0]) {
      return +itemPath[1] > +dropPath[1]
    }
    return +itemPath[0] > +dropPath[0]
  }

  if (itemPath.length === 3) {
    if (+itemPath[0] === +dropPath[0]) {

      if (itemPath.length > dropPath.length) {
        return true;
      }

      if (+itemPath[1] === +dropPath[1]) {

        // Item is being dragged to a higher scope
        if (itemPath.length > dropPath.length) {
          // If an item is moved higher, it will look like this
          // itemPath: [1,1,0]
          // dropPath: [1,1]
          return true;
        }

        return +itemPath[2] < +dropPath[2]
      }

      return +itemPath[1] > +dropPath[1]
    }
    return +itemPath[0] > +dropPath[0]
  }
}

export const moveGroup = (layout: Layout, itemPath: Path, dropPath: Path): MutationReturn => {
  const clonedLayout = [ ...layout ];

  const isReording = dropPath.length === 1;
  const isMerging = dropPath.length === 2;

  const movedGroup: Group | Asset = clonedLayout[+itemPath[0]];

  if (!isGroup(movedGroup)) {
    return [ layout, [] ]; 
  }

  const movedAssets: Asset[] = getAssetsFromGroup(movedGroup);

  let dropOrderPosition = getDropOrderPosition(layout, dropPath);
  const isMovingItemUp = isMovingItemUpwards(itemPath, dropPath);

  // If we move an item downwards, we need to add subtract one from its new order
  if (!isMovingItemUp) {
    dropOrderPosition -= 1;
  }

  // There's two places a group could be moved to
  // 1. Another top level index - so we are just reordering
  // 2. A second level index - the group is being merged

  if (isReording) {
    // all we need to do is update the order and remove the old group from the layout
    const movedAssetsWithOrder: Asset[] = movedAssets.map(( asset ) => ({ ...asset, order: dropOrderPosition++ }));
  
    // remove the old group from the layout
    const updatedLayout: Layout = removeFromLayoutByPath(clonedLayout, dropPath);

    return [ updatedLayout, movedAssetsWithOrder ];
  }

  if (isMerging) {
    const { id: dropGroupId } = clonedLayout[+dropPath[0]];
    
    // all we need to do is update the order and group id
    const movedAssetsWithOrder: Asset[] = movedAssets.map(( asset ) => ({
      ...asset,
      order: dropOrderPosition++,
      group_id: dropGroupId,
    }));

    // remove the old group from the layout
    const updatedLayout: Layout = removeFromLayoutByPath(clonedLayout, dropPath);

    return [ updatedLayout, movedAssetsWithOrder ];
  }
}

export const moveTopLevelAsset = (layout: Layout, itemPath: Path, dropPath: Path): MutationReturn => {
  const clonedLayout = [ ...layout ];
  const isReordering = dropPath.length === 1;
  const isMergingIntoGroup = dropPath.length > 1;

  let dropOrderPosition = getDropOrderPosition(layout, dropPath);
  const isMovingItemUp = isMovingItemUpwards(itemPath, dropPath);

  // If we move an item downwards, we need to add substract one from its new order
  if (!isMovingItemUp) {
    dropOrderPosition -= 1;
  }

  const movedAsset = clonedLayout[+itemPath[0]];

  if (!isAsset(movedAsset)) {
    return [ layout, [] ];
  }

  // We are just moving the asset at the top level, it's not being merged into anything
  if (isReordering) {
    // add it's new order
    const movedAssetWithOrder: Asset[] = [{ ...movedAsset, order: dropOrderPosition++ }];

    // remove the old group from the layout
    const updatedLayout: Layout = removeFromLayoutByPath(clonedLayout, itemPath);

    return [ updatedLayout, movedAssetWithOrder ];
  }

  // The top level asset is being merged into the group as a new row or into an existing group row
  if (isMergingIntoGroup) {
    const isBeingMergedAsANewRow = dropPath.length === 2;
    const isBeingMergedIntoExistingRow = dropPath.length === 3;

    // We need the drop group id to add to the moved asset
    const { id: dropGroupId } = clonedLayout[+dropPath[0]];

    if (isBeingMergedAsANewRow) {
      // update the moved asset with the group_id and create a new row id
      const updatedAsset: Asset = {
        ...movedAsset,
        order: dropOrderPosition++,
        group_id: dropGroupId,
        row_id: uuid(),
      }

      // remove the original asset from the layout
      const updatedLayout: Layout = removeFromLayoutByPath(clonedLayout, itemPath);

      return [ updatedLayout, [ updatedAsset ]];
    }
    
    if (isBeingMergedIntoExistingRow) {
      const { id: dropGroupId } = clonedLayout[+dropPath[0]];
      const { id: groupRowId } = (clonedLayout[+dropPath[0]] as Group).children[+dropPath[1]];

      const updatedAsset: Asset = {
        ...movedAsset,
        order: dropOrderPosition++,
        group_id: dropGroupId,
        row_id: groupRowId,
      }

      // remove the original asset from the layout
      const updatedLayout: Layout = removeFromLayoutByPath(clonedLayout, itemPath);

      return [ updatedLayout, [ updatedAsset ]];
    }
  }
}

export const moveAssetWithinGroup = (layout: Layout, itemPath: Path, dropPath: Path): MutationReturn => {
  const clonedLayout = [ ...layout ];

  const itemGroupIndex = +itemPath[0];
  const itemRowIndex = +itemPath[1];
  const itemChildIndex = +itemPath[2];

  const isDroppedAsTopLevelItem = dropPath.length === 1;
  const isDroppedAsNewRow = dropPath.length === 2;
  const isDroppedIntoExistingRow = dropPath.length === 3;
  const isMovingItemUp = isMovingItemUpwards(itemPath, dropPath);
  let dropOrderPosition = getDropOrderPosition(layout, dropPath);

  const affectedRow = (clonedLayout[itemGroupIndex] as Group).children[itemRowIndex];

  // If we move an item downwards, we need to add subtract one from its new order
  if (!isMovingItemUp) {
    dropOrderPosition -= 1;
  }

  const movedAsset: Asset = affectedRow.children[itemChildIndex];

  if (isDroppedAsTopLevelItem) {
    // remove the group_id and row_id
    const updatedAsset: Asset = { ...movedAsset, group_id: null, row_id: null, order: dropOrderPosition++ };
    // remove the existing asset from the layout
    const updatedLayout: Layout = removeFromLayoutByPath(clonedLayout, itemPath);

    return [ updatedLayout, [ updatedAsset ]];
  }

  // Dropped as a brand-new row inside a group
  if (isDroppedAsNewRow) {
    const dropGroupIndex = +dropPath[0];
    const { id: dropGroupId } = clonedLayout[dropGroupIndex];

    const updatedAsset: Asset = { ...movedAsset, group_id: dropGroupId, row_id: uuid(), order: dropOrderPosition++ };
    const updatedLayout: Layout = removeFromLayoutByPath(clonedLayout, itemPath);

    return [ updatedLayout, [ updatedAsset ]];
  }

  // Is dropped as a child of an already existing row
  if (isDroppedIntoExistingRow) {
    const dropGroupIndex = +dropPath[0];

    const { id: dropGroupId } = clonedLayout[dropGroupIndex];
    const { id: dropGroupRowId } = (clonedLayout[dropGroupIndex] as Group).children[+dropPath[1]];

    const updatedAsset: Asset = {
      ...movedAsset,
      group_id: dropGroupId,
      row_id: dropGroupRowId,
      order: dropOrderPosition++,
    };

    const updatedLayout: Layout = removeFromLayoutByPath(clonedLayout, itemPath);

    return [ updatedLayout, [ updatedAsset ]];
  }
}

export const groupSelectedItems = (layout: Layout, selectedAssets: SelectedAssets): MutationReturn => {
  const clonedLayout = [ ...layout ];
  const formattedSelectedAssets = formatSelectedPaths(selectedAssets);

  // we need to order the selected assets from highest to lowest order
  const orderedSelectedAssets: PathArray = orderSelectedAssetsDesc(formattedSelectedAssets);

  // The drop position will be the lowest order of the selected assets
  const dropPosition: string = orderedSelectedAssets.at(-1)[0];
  
  let startingDropOrder = getDropOrderPosition(layout, [ dropPosition ]);

  // get all the assets we want to combine into a group
  let movedAssets: (Asset | Group)[] = orderedSelectedAssets.map((path) => getAssetByPath(clonedLayout, path));

  const cleanMovedAssets = (assets): (Asset | Group)[] => {
    return assets.map((asset: Asset | Group) => {
      if (asset?.level === ItemLevel.GROUP) {
        const assets = [];

        for (const row of asset.children) {
          assets.push(row.children);
        }

        return assets.flat();
      }

      return asset;
    })
  }

  movedAssets = cleanMovedAssets(movedAssets).flat();

  // generate a new id for the group
  const group_id = uuid();

  // add the new group id to the asset, update the order and generate a new row id
  const updatedAssets: Asset[] = movedAssets.reverse().map(( asset ) => ({
    ...asset,
    group_id,
    row_id: uuid(),
    order: startingDropOrder++,
  })) as Asset[];

  // remove all the old assets from the layout
  let updatedLayout: Layout = removeMultipleAssetsByPathFromLayout(clonedLayout, orderedSelectedAssets);

  let insertPosition = getDropOrderPosition(layout, [ dropPosition ]);
  updatedLayout.splice(insertPosition, 0, ...updatedAssets);

  return [ updatedLayout, updatedAssets ];
}


export const updateSingleItem = (layout: Layout, dropPath: Path, itemPath: Path): MutationReturn => {
  const clonedLayout = [ ...layout ];

  const isMovingTopLevelItem = itemPath.length === 1;
  const isMovingGroupItem = itemPath.length === 3;

  // Moving a top level item
  // This could be an asset or a group
  if (isMovingTopLevelItem) {
    // Get the thing we want to move
    const movedItem = clonedLayout[+itemPath[0]];

    if (isGroup(movedItem)) {
      return moveGroup(layout, itemPath, dropPath)
    }

    if (isAsset(movedItem)) {
      return moveTopLevelAsset(layout, itemPath, dropPath)
    }
  }

  // Moving a child of a group row
  if (isMovingGroupItem) {
    return moveAssetWithinGroup(layout, itemPath, dropPath);
  }
}

export const moveMultipleAssets = (layout: Layout, selectedAssets: SelectedAssets, dropPath: Path): MutationReturn => {
  const clonedLayout = [ ...layout ];
  const formattedSelectedAssets = formatSelectedPaths(selectedAssets);

  const isDroppedAsTopLevelItem = dropPath.length === 1;
  const isDroppedAsNewRow = dropPath.length === 2;

  const orderedSelectedAssets = orderSelectedAssetsDesc(formattedSelectedAssets);

  const movedAssets: Asset[] = orderedSelectedAssets.map((path) => getAssetByPath(clonedLayout, path));

  let startingDropOrder = getDropOrderPosition(clonedLayout, dropPath);

  const hasMovedDown = movedAssets[movedAssets.length-1].order < startingDropOrder;

  if (hasMovedDown) {
    startingDropOrder--;
  }

  const reversedAssets = movedAssets.reverse();

  if (isDroppedAsTopLevelItem) {
    const updatedAssets: Asset[] = reversedAssets.map((asset, idx) => {
      const hasMovedDown = startingDropOrder > asset.order;
      const prevHasMovedDown = idx !== 0 && startingDropOrder > reversedAssets[idx-1].order

      if (prevHasMovedDown && !hasMovedDown) { // This is the beginning of assets moving up. Previous assets moved down. Need to adjsut startingDropOrder for this change in direction.
        startingDropOrder++;
      }

      return {
        ...asset,
        group_id: null,
        row_id: null,
        order: hasMovedDown ? startingDropOrder : startingDropOrder++,
      }
    })

    // remove all the old items from the layout
    const updatedLayout: Layout = removeMultipleAssetsByPathFromLayout(clonedLayout, orderedSelectedAssets);

    return [ updatedLayout, updatedAssets ];
  }

  if (isDroppedAsNewRow) {
    const { id: dropGroupId } = clonedLayout[+dropPath[0]];

    const updatedAssets: Asset[] = reversedAssets.map((asset, idx) => {
      const hasMovedDown = startingDropOrder > asset.order;
      const prevHasMovedDown = idx !== 0 && startingDropOrder > reversedAssets[idx-1].order

      if (prevHasMovedDown && !hasMovedDown) { // This is the beginning of assets moving up. Previous assets moved down. Need to adjsut startingDropOrder for this change in direction.
        startingDropOrder++;
      }

      return {
        ...asset,
        group_id: dropGroupId,
        row_id: uuid(),
        order: hasMovedDown ? startingDropOrder : startingDropOrder++,
      }
    })

    // remove all the old items from the layout
    const updatedLayout: Layout = removeMultipleAssetsByPathFromLayout(clonedLayout, orderedSelectedAssets);
      
    return [ updatedLayout, updatedAssets ];
  }
}

export const countAssetsInGroup = (group: Group): number => {
  let count = 0;

  for (const row of group.children) {
    count += row.children.length;
  }

  return count;
}

export const ungroupSelectedItems = (data: Layout, path: string) => {
  const layout = [ ...data ];

  const selectedGroup: Group = layout[path];

  const ungroupedAssets: Asset[] = getAssetsFromGroup(selectedGroup);
  let dropPosition: number = getDropOrderPosition(layout, [path]);

  const cleanedAssets: Asset[] = ungroupedAssets.map((asset) => ({
    ...asset,
    group_id: null,
    row_id: null,
    order: dropPosition++,
  }));

  // remove group from layout
  layout.splice(+path, 1);

  // add assets back in at the top level
  layout.splice(+path, 0, ...cleanedAssets);
  
  return [ layout, cleanedAssets ];
}

export const formatPath = (path: string): Path => {
  if (!path.includes('-')) {
    return [path];
  }
  return path.split('-') as Path;
}

export const formatSelectedPaths = (selectedAssets: string[]) => (
  selectedAssets.map((path: string) => formatPath(path))
) as PathArray;
